import React, { useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Message, Button, Modal, Dropdown } from 'semantic-ui-react';
import produce from 'immer';
import { useQueryClient } from '@tanstack/react-query';
import { postWithAuth } from 'src/api';
import { LPTable } from 'src/components/tables/LPTable';
import { useLpTable } from 'src/components/tables/internal/useLpTable';
import { createColumnHelper } from '@tanstack/react-table';
import { SelectCell } from 'src/components/tables/internal/SelectCell';
import { SELECT_WIDTH } from 'src/components/tables/internal/constants';
import {
  createPTPTableQueryKey,
  createPMPLinkTableQueryKey,
  createMeshTableQueryKey,
} from 'src/components/tables/internal/TableQueryKeys';
import { GREY_COLOR } from 'src/app.constants';
import { pathToKind } from 'src/utils/useful_functions';
import { setDirtyObjects } from '../mainframe/mainframe.reducer';

const KIND_TO_QUERY_KEY: Record<
  string,
  (projectId: string) => string[]
> = {
  ptp: createPTPTableQueryKey,
  pmp: createPMPLinkTableQueryKey,
  mesh: createMeshTableQueryKey,
};

function getStickyColumnStyle(width: number | null, left: number) {
  return {
    position: 'sticky' as const,
    left,
    backgroundColor: 'inherit',
    zIndex: 2,
    width: width ? `${width}px` : undefined,
    minWidth: width ? `${width}px` : undefined,
  };
}

function buildPathList(links: any, kind: string) {
  return links.map(({ properties: path }: any) => ({
    id: path.id,
    name: path.name,
    kind: kind,
    loc_lat: path.loc_lat,
    loc_lng: path.loc_lng,
    rem_lat: path.rem_lat,
    rem_lng: path.rem_lng,
    strokeColor: path.strokeColor,
  }));
}

function gatherProfiles(links: any) {
  return [
    ...buildPathList(links.ptpLinks.features, 'ptp'),
    ...buildPathList(links.pmpLinks.features, 'pmp'),
    ...buildPathList(links.meshLinks.features, 'mesh'),
  ];
}

const KIND_LABELS: Record<string, string> = {
  ptp: 'PTP',
  pmp: 'PMP',
  mesh: 'Mesh',
};

function KindFilterDropdown({
  column,
  kinds,
}: {
  column: any;
  kinds: string[];
}) {
  const value = (column.getFilterValue() as string) ?? '';
  const options = [
    { key: '__all', text: 'All', value: '' },
    ...kinds.map((k) => ({
      key: k,
      text: KIND_LABELS[k] ?? k,
      value: k,
    })),
  ];
  return (
    <Dropdown
      selection
      compact
      fluid
      options={options}
      value={value}
      onChange={(_, data) => column.setFilterValue(data.value)}
    />
  );
}

// Module scope: createColumnHelper() holds no per-render state, and a fresh helper
// each render would destabilise the columns memo below (see useSiteColumns).
const columnHelper = createColumnHelper<any>();

export const ProjectProfiles = ({ projectId, onClose }) => {
  const dispatch = useDispatch();
  const qc = useQueryClient();
  const links = useSelector(
    (state: any) => ({
      ptpLinks: state.mainFrame.ptpLinks,
      pmpLinks: state.mainFrame.pmpLinks,
      meshLinks: state.mainFrame.meshLinks,
    }),
    shallowEqual
  );

  const profiles = useMemo(() => gatherProfiles(links), [links]);

  const uniqueKinds = useMemo(
    () => Array.from(new Set(profiles.map((p: any) => p.kind))),
    [profiles]
  );

  // The PTP/PMP/Mesh tables read row data from the react-query cache, not
  // from the redux map slices, so the loader in SelectCell only fires when
  // the cached row's strokeColor flips to GREY_COLOR. Patch the cache here
  // to match the bulk-edit pattern used by PTPLinksTable / PMPLinksPanel.
  const setTableStrokeColor = (items: any[], color: string | null) => {
    const idsByKind = new Map<string, Set<string>>();
    for (const item of items) {
      const set = idsByKind.get(item.kind) ?? new Set<string>();
      set.add(item.id);
      idsByKind.set(item.kind, set);
    }

    for (const [kind, ids] of idsByKind) {
      const keyBuilder = KIND_TO_QUERY_KEY[kind];
      if (!keyBuilder) continue;
      qc.setQueryData(keyBuilder(projectId), (prev: any) => {
        if (!prev?.rows) return prev;
        return produce(prev, (draft: any) => {
          for (const row of draft.rows) {
            if (ids.has(row.id)) {
              row.strokeColor = color;
            }
          }
        });
      });
    }
  };

  const handleRefresh = async (items: any[]) => {
    const path_ids = items.map((item) => item.id);

    // Update strokeColor to grey for refreshing items
    const linkIds = items.map((item) => ({
      id: item.id,
      kind: pathToKind(item.kind) ?? item.kind,
    }));
    dispatch(setDirtyObjects({ objects: linkIds, value: true }));
    setTableStrokeColor(items, GREY_COLOR);

    try {
      await postWithAuth(`profile/${projectId}/refresh`, { path_ids });
    } catch (error) {
      console.error('Error refreshing profiles:', error);
      dispatch(setDirtyObjects({ objects: linkIds, value: false }));
      setTableStrokeColor(items, null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <SelectCell kind="header" table={table} />,
        cell: ({ row, table }) => (
          <SelectCell kind="row" row={row} table={table} />
        ),
        enableResizing: false,
        enableHiding: false,
        meta: {
          tdStyle: getStickyColumnStyle(SELECT_WIDTH, 0),
        },
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        meta: { flex: true },
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('kind', {
        id: 'kind',
        header: 'Link Kind',
        size: 120,
        filterFn: 'equalsString',
        meta: {
          customFilter: (col: any) => (
            <KindFilterDropdown column={col} kinds={uniqueKinds} />
          ),
        },
        cell: (info) => KIND_LABELS[info.getValue()] ?? info.getValue(),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        size: 40,
        enableResizing: false,
        cell: ({ row }) => (
          <Button
            icon="refresh"
            size="mini"
            title="Refresh Profile"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh([row.original]);
            }}
          />
        ),
      }),
    ],
    [uniqueKinds]
  );

  const visibleColumns = useMemo(
    () => ['select', 'name', 'kind', 'actions'],
    []
  );

  const tableModel = useLpTable({
    data: profiles || [],
    visibleColumns,
    columns,
    getRowId: (row) => row.id,
    options: {
      defaultPinnedColumn: 'name',
    },
  });

  const hasProfiles = profiles.length > 0;

  return (
    <Modal open={true} onClose={onClose} size="large">
      <Modal.Header>
        Project Profiles
        <Button
          circular
          icon="close"
          title="Close"
          floated="right"
          onClick={onClose}
        />
      </Modal.Header>
      <Modal.Content>
        {hasProfiles ? (
          <div style={{ height: '600px' }}>
            <LPTable
              queryKey={['project', projectId, 'profiles']}
              model={tableModel}
              tableId="project_profiles_table"
              showChooseColumns={false}
            ></LPTable>
          </div>
        ) : (
          <Message info>
            <p>No project-specific profiles found.</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        {hasProfiles && (
          <Button
            color="red"
            content="Refresh All Profiles"
            onClick={() => {
              handleRefresh(profiles || []);
              onClose();
            }}
          />
        )}
        {hasProfiles && (
          <Button
            color="blue"
            content="Refresh Selected Profiles"
            disabled={tableModel.table.getSelectedRowModel().rows.length === 0}
            onClick={() => {
              const selectedRows = tableModel.table.getSelectedRowModel().rows;
              handleRefresh(selectedRows.map((row) => row.original));
              tableModel.table.resetRowSelection();
              onClose();
            }}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};
